import { useMemo } from "react";

import Link from "@whoppah/next/link";
import { Trans } from "next-i18next";
import { useRouter } from "next/router";

import { useStripeAccountDetails } from "@/Apollo/hooks/useStripeAccountDetails";
import { SellerAccountStatus } from "@/Apollo/schema";
import { IconStateInformation } from "@/assets/icons/figma/IconStateInformation";
import { AccountSettingsViewProps } from "@/common/contracts/account.contracts";
import { useLanguage } from "@/common/hooks/pages.hooks";
import { CONTACT_US_ROUTE } from "@/common/static/routes.static";
import { AccountSettingsWrapper } from "@/components/Account/AccountSettingsWrapper";
import { BankAccountForm } from "@/components/Account/payment/BankAccountForm";
import { BankAccountSection } from "@/components/Account/payment/BankAccountSection";
import { MoneyBreakdown } from "@/components/Account/payment/MoneyBreakdown";
import { PayoutsList } from "@/components/Account/payment/PayoutsList";
import {
  FetchingStatusCard,
  StatusCard,
  StatusCardSkeleton,
} from "@/components/Account/payment/StatusCard";
import { StatusQuestions } from "@/components/Account/payment/StatusQuestions";
import { TaskChecklist } from "@/components/Account/payment/TaskChecklist";
import {
  amountLine,
  deadlinePanel,
  detailsCurrency,
  expectationTasks,
  issueTasks,
  reviewTasks,
  sellerBalance,
  slowReviewPanel,
  staleLabel,
} from "@/components/Account/payment/payment.presentation";
import {
  StripeLinkType,
  fromGatewayActions,
  readPaymentPageQuery,
  showsMoneyBlock,
  showsRecentPayouts,
  showsTaskChecklist,
} from "@/components/Account/payment/payment.utils";
import { useHeldOrders } from "@/components/Account/payment/useHeldOrders";
import { usePaymentCopy } from "@/components/Account/payment/usePaymentCopy";
import { useReasonHighlight } from "@/components/Account/payment/useReasonHighlight";
import { useStripeRedirect } from "@/components/Account/payment/useStripeRedirect";
import {
  useStripeRefresh,
  useStripeReturn,
} from "@/components/Account/payment/useStripeRoundTrip";
import { groupIssuesByAction } from "@/components/Account/stripeVerification.utils";
import { InfoPanel } from "@/ui/InfoPanel";
import { Heading } from "@/ui/cva/Heading";
import { Body } from "@/ui/typography/Body";
import { NetworkStatus } from "@apollo/client";

const TASKS_SECTION_ID = "payment-tasks";
const BANK_SECTION_ID = "payment-bank-account";

/**
 * Payment settings v2 (spec approved 2026-10-01). Block order: status card
 * with the one primary button; what Stripe asks for; what is waiting; bank
 * account; recent payouts; three questions; help. The status and the buttons
 * are the gateway's (`account_status`, `actions`), the same values the
 * dashboard, My sales and the apps read; this page only lays them out.
 */
export const PaymentSettingsPage = ({ title }: AccountSettingsViewProps) => {
  const { copy, key, defaults } = usePaymentCopy();
  const router = useRouter();
  const { locale } = router;
  const { language } = useLanguage();
  const query = readPaymentPageQuery(router.query);
  const { redirect, redirecting } = useStripeRedirect();

  const { data, loading, refetch, startPolling, stopPolling, networkStatus } =
    useStripeAccountDetails({
      // Back from Stripe the seller just changed something: every poll
      // re-reads Stripe instead of waiting out the mirror's refresh window.
      variables: { lang: language, fresh: query.stripeReturn },
      notifyOnNetworkStatusChange: true,
    });
  const details = data?.stripeAccountDetails ?? null;

  const { fetching, timedOut } = useStripeReturn({
    active: query.stripeReturn,
    details,
    startPolling,
    stopPolling,
  });
  useStripeRefresh({
    active: query.stripeRefresh,
    detailsSubmitted: details?.details_submitted ?? null,
    redirect,
  });
  const { tasksRef, bankRef } = useReasonHighlight(
    query.highlight,
    details !== null
  );

  // Always the gateway's status; a timed-out return only adds a panel.
  const status: SellerAccountStatus | null = details?.account_status ?? null;

  const groups = useMemo(
    () => groupIssuesByAction(details?.verification_issues ?? []),
    [details]
  );
  const heldOrders = useHeldOrders(
    status !== null && showsMoneyBlock(status),
    language
  );

  const handleStripeLink = (linkType: StripeLinkType) => {
    void redirect(
      linkType,
      details
        ? {
            status: details.account_status,
            asOf: details.verification_as_of ?? null,
          }
        : null
    );
  };

  const helpLine = (
    <Body as="p" color="muted">
      <Trans
        i18nKey={key("helpLine")}
        defaults={defaults("helpLine")}
        components={{
          support: <Link href={CONTACT_US_ROUTE} className="underline" />,
        }}
      />
    </Body>
  );

  if (!details || status === null) {
    return (
      <AccountSettingsWrapper title={title}>
        {loading ? (
          query.stripeReturn ? (
            <FetchingStatusCard />
          ) : (
            <StatusCardSkeleton />
          )
        ) : (
          helpLine
        )}
      </AccountSettingsWrapper>
    );
  }

  const now = new Date();
  const { primary, secondary } = fromGatewayActions(details.actions, status);
  const stale = staleLabel(details, now, copy, locale);

  const bankAccount = details.bank_account ?? null;
  const bankGroup =
    groups.find(group => group.cta.kind === "bank-form") ?? null;
  const bankErrored = bankAccount?.status === "errored";
  const bankTaskNeeded =
    showsTaskChecklist(status) && (bankGroup !== null || !bankAccount);
  const bankTaskIssue = bankGroup?.issues[0] ?? null;
  const bankForm = (
    <BankAccountForm
      bankAccount={bankAccount}
      clearNumber={bankErrored}
      primary={
        primary.kind === "bank-form" ||
        (primary.kind === "stripe-link" && primary.disabled)
      }
      onSaved={() => refetch()}
    />
  );

  return (
    <AccountSettingsWrapper title={title}>
      {timedOut && (
        <InfoPanel
          color="curious"
          icon={<IconStateInformation className="size-6 shrink-0" />}
          body={<p>{copy("fetchingTimedOut")}</p>}
        />
      )}
      {fetching ? (
        <FetchingStatusCard />
      ) : (
        <StatusCard
          status={status}
          title={details.status_title}
          description={details.status_description}
          stale={
            stale
              ? {
                  label: stale,
                  onCheckAgain: () => void refetch({ fresh: true }),
                  checking: networkStatus === NetworkStatus.refetch,
                }
              : null
          }
          amountLine={amountLine(details, status, copy, locale)}
          deadline={deadlinePanel(details, status, now, locale)}
          slowReview={slowReviewPanel(details, status, now)}
          primary={primary}
          secondary={secondary}
          onStripeLink={handleStripeLink}
          redirecting={redirecting}
        />
      )}

      {showsTaskChecklist(status) && (
        <TaskChecklist
          ref={tasksRef}
          id={TASKS_SECTION_ID}
          title={copy("sectionStripeAsks")}
          tasks={
            status === SellerAccountStatus.NotSetUp
              ? expectationTasks(copy)
              : issueTasks(groups, copy)
          }
          bankTask={
            bankTaskNeeded
              ? {
                  title: bankTaskIssue?.title ?? copy("bankTaskTitle"),
                  description:
                    bankTaskIssue?.description ?? copy("bankTaskWhy"),
                  form: bankForm,
                }
              : null
          }
          highlighted={
            query.highlight === "tasks" ||
            (query.highlight === "bank" && bankTaskNeeded)
          }
        />
      )}

      {status === SellerAccountStatus.UnderReview && (
        <TaskChecklist
          id={TASKS_SECTION_ID}
          title={copy("sectionBeingChecked")}
          tasks={reviewTasks(details, status, copy, locale)}
          bankTask={null}
        />
      )}

      {showsMoneyBlock(status) && (
        <MoneyBreakdown
          status={status}
          orders={heldOrders}
          balance={sellerBalance(details)}
          currency={detailsCurrency(details)}
        />
      )}

      {!bankTaskNeeded &&
        (bankAccount ? (
          <BankAccountSection
            ref={bankRef}
            id={BANK_SECTION_ID}
            bankAccount={bankAccount}
            accountCreated={details.account_created}
            onSaved={() => refetch()}
            highlighted={query.highlight === "bank"}
          />
        ) : (
          <section
            id={BANK_SECTION_ID}
            ref={bankRef}
            aria-labelledby={`${BANK_SECTION_ID}-title`}
            className="flex scroll-mt-24 flex-col gap-3"
          >
            <Heading as="h2" size="h5" id={`${BANK_SECTION_ID}-title`}>
              {copy("sectionBankAccount")}
            </Heading>
            <Body as="p" color="muted">
              {copy("bankNone")}
            </Body>
            {bankForm}
          </section>
        ))}

      {showsRecentPayouts(status) && <PayoutsList />}

      <StatusQuestions status={status} />

      {helpLine}
    </AccountSettingsWrapper>
  );
};
